import * as React from 'react';
import * as Images from 'pos/Images';
import { PosScreen, Header, Content, LargeDeviceOnly, Footer, PhoneOnly, BackActionButton, Filler, AcceptActionButton } from 'pos/ui';
import { Res, TicketLineDTO, TicketLineStatus, Permission, SoldProductDTO } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { CustomerDisplay } from 'pos/services';
import { getWeight } from '../product-composition/product';
import { CatalogueFacade } from 'pos/facades';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { zQuantity } from 'core/infrastructure-next';
import { VStack, Text, Box } from '@chakra-ui/react';
import { BasicButton, SvgIcon } from 'core/ui-next';
import * as UI from 'core/ui';

type Props = {
	line: TicketLineDTO;
} & Modal.Actions<number>;

const stateSchema = z.object({
	initialAmount: zQuantity({ nonNegative: false, decimalPlaces: 3 })
});

type State = z.infer<typeof stateSchema>;

function userCannotChangeQuantityInOrderedLines(): boolean {
	return !Preconditions.ensure(Permission.Ticket_SetLineQuantityInOrderedLines)();
}

async function isSoldByWeight(Product: SoldProductDTO): Promise<boolean> {
	// Puedo ignorar el ticketId porque se usa para calcular precios, y aquí realmente lo que quiero es saber si el producto
	// se vende al peso o no
	const ignoredTicketId = 0;
	const { IsSoldByWeight } = await CatalogueFacade.getSaleableAsMainBySaleFormatId(Product.SaleFormatId, ignoredTicketId);
	return IsSoldByWeight;
}

async function shouldNotifyInKitchen(line: TicketLineDTO): Promise<boolean> {
	if (line.Status === TicketLineStatus.New) {
		return false;
	}

	const shouldAsk = await SalesStore.shouldAskBeforeNotifyQuantityChanged(line);
	if (!shouldAsk)
		return true;

	const result = await Message.confirm(Res.Msg.MSG_NOTIFY_QUANTITY_CHANGED_IN_KITCHEN());
	return result.ok;
}

function GetQuantity(props: Props) {
	const { line, cancel, close } = props;
	const formRef = React.useRef<FormRef<State>>(null);
	const { Form, DecimalInput } = useControlsFor<State>();

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close(result.value.initialAmount);
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='SET_TICKET_LINE_QUANTITY' size='small' useKeyPad={true}>
		<Header title={Res.Web.EDIT_TICKET_LINE_MODIFY_QUANTITY_LBL()} onClose={cancel} />
		<Content>
			<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
				<VStack gap={'4'} justifyItems={'center'} w='full'>
					<SvgIcon src={Images.EditQuantity} w={'12'} h={'12'} />
					<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
						<Text fontSize={'md'} fontWeight={'bold'}>
							{Res.Web.EDIT_TICKET_LINE_NEW_QUANTITY_LBL()}
						</Text>
					</VStack>
				</VStack>
				<VStack w={'full'} justifyContent={'center'}>
					<Box w={'full'}>
						<Form ref={formRef} initialValue={{ initialAmount: line.Quantity }} schema={stateSchema}>
							<DecimalInput linkTo='initialAmount' onKeyDown={UI.whenEnterKey(onAccept)} />
						</Form>
					</Box>
				</VStack>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
				</LargeDeviceOnly>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}

declare global {
	interface CommandType {
		setTicketLineQuantity: (line: TicketLineDTO, quantity?: number) => Promise<Result>
	}
}

Command.register('setTicketLineQuantity', Preconditions.ensure(Permission.Ticket_SetQuantity), async (line: TicketLineDTO, quantity?: number): Promise<Result> => {

	if (line.Status !== TicketLineStatus.New && userCannotChangeQuantityInOrderedLines())
		return Result.error();

	if (quantity === undefined) {

		const quantityResult = await isSoldByWeight(line.Product) ? await getWeight(line.Product) : await Modal.show(GetQuantity, { line });
		if (!quantityResult.ok)
			return Result.error();
		quantity = quantityResult.value;
	}

	if (quantity === 0) {
		await Message.show(Res.Msg.MSG_CANNOT_ESTABLISH_ZERO_QUANTITY_LINE());
		return Result.error();
	}

	if (quantity < 0 && !SessionProvider.hasPermission(Permission.Ticket_AllowNegativeLines)) {
		await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.Ticket_AllowNegativeLines)));
		return Result.error();
	}

	const notifyInKitchen = await shouldNotifyInKitchen(line);
	await SalesStore.setLineQuantity(line, quantity, notifyInKitchen);
	await CustomerDisplay.displayExistingLine(SalesStore.getTicket(), SalesStore.getLineById(line.Id));
	return Result.ok();

});
